import Div from '@/shared/Div';
import React, { memo, useCallback, useEffect, useMemo } from 'react';
import Metadata from '@/shared/crisp/Metadata';
import Button from '@/shared/Button';
import { getFormattedPrice } from '@/utils/price';
import Price from './Price';
import OfferCard from '@/shared/crisp/OfferCard';
import CouponList from '@/shared/crisp/CouponsList';
import useModalToggle from '@/hooks/useModalToggle';
import Checked from '@/components/Icons/Checked';
import Paragraph from '@/shared/Paragraph';
import CrossIcon from '@/components/Icons/Cross';
import CouponAppliedModal from '@/shared/crisp/CouponAppliedModal';
import { useEcomProductContext } from '@/context/ecomProductContext';
import useEcomProduct from '@/hooks/useEcomProduct';
import Map from '../Map';
import DiscountCoupon from '../Icons/DiscountCoupon';
import FeaturedCoupon from '@/shared/crisp/FeaturedCoupon';
import OfferCardIcons from '@/shared/crisp/OfferCardIcons';
import { isInCart, isOutOfStock } from '@/utils/product';
import { useProfileContext } from '@/context/profileContext';
import is from '@/utils/is';
import { PDP_COUPON_MODAL_OPEN } from '@/constants/app';
import { hasHashParam, removeHashParam } from '@/utils/url';
import Bank from '@/components/Icons/Bank';
import LocalStorage from '@/utils/localStorage';
import { EXISTING_PREXO_DETAILS } from '@/constants/app';

interface DealTagAttributes {
  label?: string;
  background?: string;
  labelColor?: string;
}
interface ProductPriceItem {
  materialId: string;
  productId: string;
  type: string;
  mrp: number;
  sellingPrice: number;
  discPercent: number;
  discText: string;
  prexo: any;
  coupons: CouponItem[];
  ncemi: any;
}

export interface CouponItem {
  coupon: string;
  applicableDisc: number;
  description: string;
  terms: string[];
  featured: boolean;
  expiry: number;
}

function AddToCart({
  dataTestId,
  dealTag,
  price,
  renderAddToCartCTA,
  materialId,
  flags,
  priceLoaded,
}: {
  dataTestId?: string;
  dealTag?: DealTagAttributes;
  price: ProductPriceItem;
  renderAddToCartCTA: (data: any) => null;
  materialId: string;
  flags: any;
  priceLoaded?: boolean;
}) {
  const { dispatch: ecomProductDispatch, state: ecomProductState } = useEcomProductContext();
  const product = useEcomProduct(materialId);
  const {
    state: { isLoggedIn },
  } = useProfileContext();
  const { commercial } = flags;
  const { discPercent = '', mrp = 0, sellingPrice = 0, productId, coupons } = price || {};
  const renderPrexo = useCallback((): any => {
    if (sellingPrice) {
      const prexoExchangeDetails = LocalStorage.get(EXISTING_PREXO_DETAILS) || {};
      const prexoValue = prexoExchangeDetails.prexoExchangeMRP;
      return getFormattedPrice(Number(sellingPrice) - Number(prexoValue));
    }
  }, [sellingPrice]);

  const removePrexo = useCallback(() => {
    ecomProductDispatch.updatePrexo({ materialId, prexo: null });
  }, [materialId]);

  const { open, openModal, closeModal } = useModalToggle(false);
  const {
    open: openApplied,
    openModal: openAppliedModal,
    closeModal: closeAppliedModal,
  } = useModalToggle(false);

  const removeAppliedCoupon = () => {
    ecomProductDispatch.updateAppliedCoupon({ materialId, appliedCoupon: null });
  };

  useEffect(() => {
    if (
      isLoggedIn &&
      priceLoaded &&
      hasHashParam(PDP_COUPON_MODAL_OPEN) &&
      !is.empty.array(price?.coupons)
    ) {
      openModal();
      removeHashParam(PDP_COUPON_MODAL_OPEN);
    }
  }, [isLoggedIn, priceLoaded]);

  const filteredCoupons = coupons?.filter((coupon) => coupon.applicableDisc > 0);

  const findFeaturedCoupon = (couponsData: CouponItem[] | undefined): CouponItem | undefined => {
    return couponsData?.find((coupon) => coupon.featured === true);
  };
  const featuredCoupon = findFeaturedCoupon(filteredCoupons);

  const data = [
    {
      title: 'Coupons',
      subTitle: `Up to ${
        featuredCoupon
          ? getFormattedPrice(featuredCoupon.applicableDisc)
          : getFormattedPrice(filteredCoupons?.[0]?.applicableDisc ?? 0)
      }`,
      ctas: [
        {
          label: `${filteredCoupons?.length} ${filteredCoupons?.length > 1 ? 'offers' : 'offer'} `,
          theme: 'none',
          id: 'coupon',
          onClick: openModal,
        },
      ],
      icon: <DiscountCoupon />,
    },

    {
      title: 'Bank offers',
      subTitle: '10% instant discount on all credit/debit cards',
      icon: <Bank />,
    },
    {
      title: 'EMI plans',
      subTitle: `No-cost EMI plans, up to ${getFormattedPrice(price?.ncemi?.ncemi)} off`,

      icon: <OfferCardIcons type={'bank'} />,
      isEMIPlansCard: true,
    },
  ];

  const renderOfferCard = useCallback((res: any, index: number) => {
    if (res.title === 'EMI plans' && !price?.ncemi?.ncemi) return null;
    if (res.title === 'Coupons' && !filteredCoupons?.length) return null;
    return (
      <OfferCard
        dataTestId={`info_card ${index}`}
        title={res.title}
        subTitle={res.subTitle}
        ctas={res.ctas}
        icon={res.icon}
        img={res.img}
        key={index}
        className="shrink-0 w-[128px] md:w-full md:shrink"
        isEMIPlansCard={res?.isEMIPlansCard || false}
      />
    );
  }, []);

  const isProductInCart = useMemo(
    () => isInCart(ecomProductState.inCartItems, productId),
    [productId, ecomProductState.inCartItems],
  );

  if (commercial) {
    return (
      <Div className="md:border md:border-solid md:border-co-subtle md:p-s6 md:rounded-cs flex flex-col gap-s6 md:bg-cs-secondary mt-s9 md:mt-s10">
        <Div
          className="px-s4 lg:px-s5 py-s5 border border-solid border-co-subtle bg-cs-primary rounded-cs "
          dataTestId={dataTestId}
        >
          {renderAddToCartCTA &&
            renderAddToCartCTA({
              className: 'w-full mt-s4',
              dataTestId: 'add-to-cart-button',
            })}
        </Div>
      </Div>
    );
  }

  const isProductOutOfStock = isOutOfStock(product);

  return (
    <Div className="md:border md:border-solid md:border-co-subtle md:p-s6 md:rounded-cs flex flex-col gap-s6 md:bg-cs-secondary mt-s9 md:mt-s10">
      <Map
        className="flex md:grid md:grid-cols-3 gap-s4 overflow-x-auto whitespace-nowrap hide-scrollbar w-full"
        list={data}
        data-testid="Main-nav"
        render={renderOfferCard}
        wrapper={'div'}
      />
      <Div
        className="px-s4 lg:px-s5 py-s5 border border-solid border-co-subtle bg-cs-primary rounded-cs "
        dataTestId={dataTestId}
      >
        {dealTag ? (
          <Metadata
            dataTestId="deal-tag"
            className="!body-xs !py-s2 !px-1.5 !lg:body-xs-desktop"
            style={{
              background: dealTag.background,
              color: dealTag.labelColor,
            }}
          >
            {dealTag.label}
          </Metadata>
        ) : null}
        <Div className="flex items-center gap-s2 lg:gap-s3 pt-s3">
          <Price
            type="addToCart"
            value={{
              sellingPrice,
              discPercent,
              mrp,
            }}
          />
        </Div>
        {product?.prexo ? (
          <Div className="flex justify-between">
            <Div className="flex align-baseline gap-s2">
              <Div className="mt-s2">
                <Checked />
              </Div>
              <Paragraph className="text-cu-positive text-body-xs-desktop leading-loose	uppercase">
                With exchange you pay&nbsp;{renderPrexo()}
              </Paragraph>
            </Div>
            {isProductInCart || isProductOutOfStock ? null : (
              <Button theme="none" onClick={removePrexo}>
                <CrossIcon />
              </Button>
            )}
          </Div>
        ) : null}
        {product?.appliedCoupon ? (
          <Div className="flex justify-between">
            <Div className="flex align-baseline gap-s2">
              <Div className="mt-s2">
                <Checked />
              </Div>
              <Paragraph className="text-cu-positive text-body-xs-desktop leading-loose	uppercase">
                {getFormattedPrice(product?.appliedCoupon?.applicableDisc)} Coupon Applied on
                Checkout
              </Paragraph>
            </Div>
            <Button theme="none" onClick={removeAppliedCoupon}>
              <CrossIcon />
            </Button>
          </Div>
        ) : null}
        {!product?.appliedCoupon ? <FeaturedCoupon featuredCoupon={featuredCoupon} /> : null}
        {renderAddToCartCTA &&
          renderAddToCartCTA({
            className: 'w-full mt-s4',
            dataTestId: 'add-to-cart-button',
          })}
      </Div>
      <CouponList
        open={open}
        onApply={openAppliedModal}
        onClose={closeModal}
        couponsData={price.coupons}
        setCart={() => {}}
      />
      <CouponAppliedModal
        open={openApplied}
        onClose={closeAppliedModal}
        couponData={product?.appliedCoupon}
      />
    </Div>
  );
}
export default memo(AddToCart);
